import React, { useState, useCallback, useEffect } from 'react';
import { InteractiveHilbert, useControlledHilbert } from '@netd-tud/hilby';
// Only import RPKI data service, no sample data
import { rpkiService, ProcessedROAData } from './rpkiData';

interface HilbertVisualizationProps {
  topPrefix?: string;
}

// Simple IPv4/IPv6 CIDR validation
const isValidCIDR = (cidr: string): boolean => {
  const cidrPattern = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$|^([0-9a-fA-F:]+)\/\d{1,3}$/;
  if (!cidrPattern.test(cidr)) return false;

  // Basic IPv4 validation
  if (cidr.includes('.')) {
    const [ip, mask] = cidr.split('/');
    const octets = ip.split('.');
    const maskNum = parseInt(mask);

    if (maskNum < 0 || maskNum > 32) return false;

    return octets.every(octet => {
      const num = parseInt(octet);
      return num >= 0 && num <= 255;
    });
  }

  return true; // Basic IPv6 validation would go here
};

const HilbertVisualization: React.FC<HilbertVisualizationProps> = ({
  topPrefix = "1.0.0.0/8"
}) => {
  const [inputValue, setInputValue] = useState(topPrefix);
  const [currentPrefix, setCurrentPrefix] = useState(topPrefix);
  const [error, setError] = useState<string>('');
  const [rpkiData, setRpkiData] = useState<ProcessedROAData>({});
  const [loadingRPKI, setLoadingRPKI] = useState<boolean>(false);
  const [rpkiStats, setRpkiStats] = useState<any>(null);

  // Load RPKI data on component mount
  useEffect(() => {
    const loadRPKIData = async () => {
      setLoadingRPKI(true);
      try {
        const data = await rpkiService.fetchRPKIData();
        setRpkiData(data);
        setRpkiStats(rpkiService.getStats());
        console.log(`Loaded ${Object.keys(data).length} ROA entries`);

        // Clear any previous errors
        setError('');
      } catch (error) {
        console.error('Failed to load RPKI data:', error);
        if (error instanceof Error && error.message && error.message.includes('not found')) {
          setError('RPKI file not found. Download it with: curl https://rpki.cloudflare.com/rpki.json > public/rpki-data.json');
        } else {
          setError('Failed to load RPKI data');
        }
      } finally {
        setLoadingRPKI(false);
      }
    };

    loadRPKIData();
  }, []);

  // Debounce input changes to avoid constant re-rendering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isValidCIDR(inputValue)) {
        setCurrentPrefix(inputValue);
        setError('');
      } else if (inputValue.trim() !== '') {
        setError('Invalid CIDR format (e.g., 10.0.0.0/8)');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleQuickSelect = (prefix: string) => {
    setInputValue(prefix);
    setCurrentPrefix(prefix);
    setError('');
  };

  const handleReloadRPKI = async () => {
    setLoadingRPKI(true);
    setError('');
    try {
      const data = await rpkiService.fetchRPKIData(true); // Force reload
      setRpkiData(data);
      setRpkiStats(rpkiService.getStats());
      console.log(`Reloaded ${Object.keys(data).length} ROA entries`);
    } catch (error) {
      console.error('Failed to reload RPKI data:', error);
      if (error instanceof Error && error.message && error.message.includes('not found')) {
        setError('RPKI file not found. Download it with: curl https://rpki.cloudflare.com/rpki.json > public/rpki-data.json');
      } else {
        setError('Failed to reload RPKI data');
      }
    } finally {
      setLoadingRPKI(false);
    }
  };

  const renderFunctions = [
    useCallback((prefix: string, long: bigint, netmask: number, config: any) => {
      // Get enhanced ROA info with hierarchy analysis
      const roaInfo = rpkiService.getROAInfo(prefix);

      const isLargerSubnet = netmask <= 16;
      const hasROA = !!roaInfo?.hasROA;
      const hasROAChildren = !!roaInfo?.hasROAChildren;
      const parentHasROA = !!roaInfo?.parentHasROA;
      const childrenCount = roaInfo?.roaChildrenCount || 0;

      // Enhanced color scheme for hierarchical ROA coverage
      let backgroundColor = 'rgba(240, 240, 240, 0.7)'; // Default gray - no ROA, no children
      let borderColor = '#ddd';
      let textColor = '#333';
      let statusText = '✗ No ROA';

      if (hasROA && hasROAChildren) {
        // Blue: Has ROA + has ROA-covered children
        backgroundColor = 'rgba(33, 150, 243, 0.9)';
        borderColor = '#2196f3';
        textColor = '#fff';
        statusText = `✓ ROA + ${childrenCount} children`;
      } else if (hasROA) {
        // Green: Direct ROA coverage
        backgroundColor = 'rgba(76, 175, 80, 0.9)';
        borderColor = '#4caf50';
        textColor = '#fff';
        statusText = '✓ ROA';
      } else if (hasROAChildren) {
        // Orange: No ROA but has ROA-covered children
        backgroundColor = 'rgba(255, 152, 0, 0.8)';
        borderColor = '#ff9800';
        textColor = '#fff';
        statusText = `⚠ ${childrenCount} ROA children`;
      } else if (parentHasROA) {
        // Light blue: No ROA but parent has ROA
        backgroundColor = 'rgba(144, 202, 249, 0.7)';
        borderColor = '#90caf9';
        textColor = '#1565c0';
        statusText = '◐ Parent ROA';
      }
      // Default gray for no ROA, no children, no parent ROA

      // Store custom data in properties
      config.properties = {
        ...config.properties,
        roaInfo: roaInfo,
        hasROA: hasROA,
        hasROAChildren: hasROAChildren,
        parentHasROA: parentHasROA,
        childrenCount: childrenCount,
        asn: roaInfo?.asn || 'Unknown'
      };

      // Enhanced tooltip with hierarchy information
      let tooltipContent = `Prefix: ${prefix}/${netmask}\n`;

      if (hasROA) {
        tooltipContent += `ASN: ${roaInfo.asn}\nMax Length: /${roaInfo.maxLength}\nTrust Anchor: ${roaInfo.ta}\nROA Status: ✓ Valid ROA\n`;
      } else {
        tooltipContent += `ROA Status: ✗ No ROA found\n`;
      }

      if (hasROAChildren) {
        tooltipContent += `Children: ${childrenCount} ROA-covered child prefixes\n`;
      }

      if (parentHasROA) {
        tooltipContent += `Parent: Covered by parent ROA\n`;
      }

      tooltipContent += `Hierarchy: ${statusText}`;

      // Apply colors to the entire box
      config.style = {
        ...config.style,
        backgroundColor: backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '2px'
      };

      config.innerContent.push(
        <div
          key={prefix}
          style={{
            padding: '2px',
            fontSize: isLargerSubnet ? '9px' : '7px',
            color: textColor,
            textAlign: 'center' as const,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '20px',
            overflow: 'hidden',
            width: '100%',
            height: '100%',
            textShadow: hasROA ? '1px 1px 1px rgba(0,0,0,0.5)' : 'none'
          }}
          title={tooltipContent}
        >
          <div style={{ fontWeight: 'bold', lineHeight: '1.1' }}>{prefix}</div>
          {roaInfo && roaInfo.asn !== 'Unknown' && (
            <div style={{ fontSize: '6px', opacity: 0.9, lineHeight: '1' }}>
              {roaInfo.asn}
            </div>
          )}
          <div style={{ fontSize: '6px', fontWeight: 'bold', lineHeight: '1' }}>
            {statusText}
          </div>
        </div>
      );
    }, [rpkiData])
  ];

  const samplePrefixes = [
    "1.0.0.0/8",      // Cloudflare - likely to have ROAs
    "8.8.8.0/24",     // Google DNS - likely to have ROAs
    "1.1.1.0/24",     // Cloudflare DNS - likely to have ROAs
    "104.16.0.0/12",  // Cloudflare CDN - likely to have ROAs
    "64.57.16.0/20",
    "163.253.0.0/22",
    "2001:468::/48"
  ];

  // Sidebar component that displays hovered prefix details
  const HoveredPrefixSidebar = ({ hoveredPrefix }: { hoveredPrefix: string | null }) => {

    if (!hoveredPrefix) {
      return (
        <div style={{
          padding: '16px',
          textAlign: 'center',
          color: '#666',
          fontSize: '14px'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏠</div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Prefix Details</div>
          <div>Hover over a subnet to see ROA information</div>
        </div>
      );
    }

    // Get enhanced ROA info with hierarchy analysis
    const roaInfo = rpkiService.getROAInfo(hoveredPrefix);
    const hasROA = !!roaInfo?.hasROA;
    const hasROAChildren = !!roaInfo?.hasROAChildren;
    const parentHasROA = !!roaInfo?.parentHasROA;
    const childrenCount = roaInfo?.roaChildrenCount || 0;

    return (
      <div style={{ padding: '16px' }}>
        <div style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '12px',
          color: '#333',
          borderBottom: '2px solid #4CAF50',
          paddingBottom: '8px'
        }}>
          📊 Prefix Details
        </div>

        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '16px',
          fontFamily: 'monospace',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          {hoveredPrefix}
        </div>

        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          {/* ROA Status */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            padding: '8px',
            backgroundColor: hasROA ? '#e8f5e8' : (hasROAChildren ? '#fff3e0' : (parentHasROA ? '#e3f2fd' : '#fff3cd')),
            borderRadius: '4px',
            border: `1px solid ${hasROA ? '#4caf50' : (hasROAChildren ? '#ff9800' : (parentHasROA ? '#2196f3' : '#ffc107'))}`
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: hasROA ? '#4caf50' : (hasROAChildren ? '#ff9800' : (parentHasROA ? '#2196f3' : '#ffc107')),
              marginRight: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold'
            }}>
              {hasROA ? '✓' : (hasROAChildren ? '⚠' : (parentHasROA ? '◐' : '!'))}
            </div>
            <span style={{ fontWeight: 'bold' }}>
              {hasROA && hasROAChildren ? 'ROA + Children' :
               hasROA ? 'ROA Found' :
               hasROAChildren ? 'ROA Children Found' :
               parentHasROA ? 'Parent ROA Coverage' :
               'No ROA Coverage'}
            </span>
          </div>

          {/* ROA Details */}
          {hasROA && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>ASN:</strong> <span style={{ fontFamily: 'monospace', color: '#4caf50' }}>{roaInfo.asn}</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Max Length:</strong> <span style={{ fontFamily: 'monospace' }}>/{roaInfo.maxLength}</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Trust Anchor:</strong> <span style={{
                  fontFamily: 'monospace',
                  textTransform: 'uppercase',
                  backgroundColor: '#e3f2fd',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '12px'
                }}>{roaInfo.ta}</span>
              </div>
            </div>
          )}

          {/* Hierarchy Information */}
          {(hasROAChildren || parentHasROA) && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>Hierarchy:</div>

              {hasROAChildren && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Children:</strong> <span style={{
                    fontFamily: 'monospace',
                    backgroundColor: '#fff3e0',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px'
                  }}>{childrenCount} ROA-covered child prefixes</span>
                </div>
              )}

              {parentHasROA && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>Parent:</strong> <span style={{
                    fontFamily: 'monospace',
                    backgroundColor: '#e3f2fd',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px'
                  }}>Covered by parent ROA</span>
                </div>
              )}
            </div>
          )}

          {/* Security Assessment */}
          <div style={{
            padding: '8px',
            backgroundColor: hasROA ? '#e8f5e8' : (hasROAChildren ? '#fff3e0' : (parentHasROA ? '#e3f2fd' : '#ffebee')),
            borderRadius: '4px',
            fontSize: '12px',
            color: hasROA ? '#2e7d32' : (hasROAChildren ? '#e65100' : (parentHasROA ? '#1565c0' : '#c62828'))
          }}>
            {hasROA && hasROAChildren ? (
              <div><strong>✓ Excellent Coverage:</strong> This prefix has ROA protection and contains {childrenCount} ROA-covered child prefixes.</div>
            ) : hasROA ? (
              <div><strong>✓ Valid ROA:</strong> This prefix is authorized for announcement by {roaInfo.asn}.</div>
            ) : hasROAChildren ? (
              <div><strong>⚠ Partial Coverage:</strong> This prefix lacks ROA but contains {childrenCount} ROA-protected child prefixes.</div>
            ) : parentHasROA ? (
              <div><strong>◐ Inherited Protection:</strong> This prefix is covered by a parent ROA but lacks specific authorization.</div>
            ) : (
              <div><strong>✗ No Protection:</strong> This prefix lacks ROA protection and may be vulnerable to BGP hijacking.</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Get controlled Hilbert hooks
  const [hilbertStore, prefixManipulation, zoomManipulation, useHoveredPrefix] = useControlledHilbert();

  // Use the hover hook at the component level
  const hoveredInfo = useHoveredPrefix();
  const currentHoveredPrefix = hoveredInfo?.prefix;

  console.log('Current hovered prefix:', currentHoveredPrefix);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header controls */}
      <div style={{
        marginBottom: '10px',
        padding: '10px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold', color: '#333', fontSize: '14px' }}>
            IP Prefix:
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="e.g., 10.0.0.0/8"
              style={{
                marginLeft: '6px',
                padding: '4px 8px',
                border: `1px solid ${error ? '#f44336' : '#ddd'}`,
                borderRadius: '3px',
                fontSize: '12px',
                width: '150px',
                fontFamily: 'monospace',
                backgroundColor: error ? '#ffebee' : 'white'
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666' }}>Quick:</span>
            {samplePrefixes.map(prefix => (
              <button
                key={prefix}
                onClick={() => handleQuickSelect(prefix)}
                style={{
                  padding: '3px 6px',
                  fontSize: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '2px',
                  backgroundColor: currentPrefix === prefix ? '#4CAF50' : 'white',
                  color: currentPrefix === prefix ? 'white' : '#333',
                  cursor: 'pointer',
                  fontFamily: 'monospace'
                }}
              >
                {prefix}
              </button>
            ))}
          </div>

          {error && (
            <div style={{
              fontSize: '10px',
              color: '#f44336',
              backgroundColor: '#ffebee',
              padding: '2px 6px',
              borderRadius: '2px',
              border: '1px solid #f44336'
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}>RPKI Coverage:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: 'rgba(76, 175, 80, 0.9)', border: '1px solid #4caf50', borderRadius: '2px' }}></div>
            <span style={{ fontSize: '8px' }}>✓ ROA</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: 'rgba(33, 150, 243, 0.9)', border: '1px solid #2196f3', borderRadius: '2px' }}></div>
            <span style={{ fontSize: '8px' }}>✓ ROA + Children</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: 'rgba(255, 152, 0, 0.8)', border: '1px solid #ff9800', borderRadius: '2px' }}></div>
            <span style={{ fontSize: '8px' }}>⚠ ROA Children</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: 'rgba(144, 202, 249, 0.7)', border: '1px solid #90caf9', borderRadius: '2px' }}></div>
            <span style={{ fontSize: '8px' }}>◐ Parent ROA</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <div style={{ width: '10px', height: '10px', backgroundColor: 'rgba(240, 240, 240, 0.7)', border: '1px solid #ddd', borderRadius: '2px' }}></div>
            <span style={{ fontSize: '8px' }}>✗ No Coverage</span>
          </div>

          {loadingRPKI ? (
            <span style={{ fontSize: '9px', color: '#2196F3', marginLeft: '8px' }}>
              🔄 Loading RPKI data...
            </span>
          ) : rpkiStats ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
              <span style={{ fontSize: '9px', color: '#4caf50' }}>
                📊 {rpkiStats.total.toLocaleString()} ROAs loaded
                {rpkiStats.total <= 11 && (
                  <span style={{ color: '#ff9800', marginLeft: '4px' }}>
                    (demo data)
                  </span>
                )}
              </span>
              <button
                onClick={handleReloadRPKI}
                style={{
                  fontSize: '8px',
                  padding: '2px 4px',
                  border: '1px solid #4caf50',
                  borderRadius: '2px',
                  backgroundColor: 'white',
                  color: '#4caf50',
                  cursor: 'pointer'
                }}
                title="Reload RPKI data"
              >
                🔄
              </button>
            </span>
          ) : (
            <span style={{ fontSize: '9px', color: '#f44336', marginLeft: '8px' }}>
              ❌ RPKI load failed
            </span>
          )}

          {error && error.includes('not found') && (
            <div style={{
              fontSize: '10px',
              color: '#ff9800',
              backgroundColor: '#fff3e0',
              padding: '4px 8px',
              borderRadius: '3px',
              border: '1px solid #ffb74d',
              marginTop: '5px',
              fontFamily: 'monospace'
            }}>
              💡 To use real RPKI data: curl https://rpki.cloudflare.com/rpki.json &gt; public/rpki-data.json
            </div>
          )}

          <span style={{ fontSize: '9px', color: '#666', marginLeft: '8px' }}>
            Drag to pan • Wheel to zoom • Hover for ROA details
          </span>
        </div>
      </div>

      {/* Main content area with sidebar */}
      <div style={{ display: 'flex', flex: 1, gap: '10px' }}>
        {/* Visualization area */}
        <div style={{
          flex: 1,
          border: '2px solid #4CAF50',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fafafa',
          minHeight: '400px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {isValidCIDR(currentPrefix) ? (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              <InteractiveHilbert
                topPrefix={currentPrefix}
                renderFunctions={renderFunctions}
                hilbertStore={hilbertStore}
              />
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              color: '#666',
              padding: '20px'
            }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>📊</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px' }}>
                Waiting for valid IP prefix
              </div>
              <div style={{ fontSize: '12px' }}>
                Enter a valid CIDR notation (e.g., 10.0.0.0/8) or use the quick buttons above
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{
          width: '300px',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'auto',
          flexShrink: 0
        }}>
          <HoveredPrefixSidebar hoveredPrefix={currentHoveredPrefix} />
        </div>
      </div>
    </div>
  );
};

export default HilbertVisualization;